import React, { useState, useEffect } from 'react';
import { getUserProfile, getMyTier, getActivePromotions } from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const [profile, setProfile] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, tierRes, promoRes] = await Promise.all([
        getUserProfile(),
        getMyTier(),
        getActivePromotions()
      ]);

      setProfile(profileRes.data);
      setTierInfo(tierRes.data);
      setPromotions(promoRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="container dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {profile?.name}! 👋</h1>
        <p>Track your points, redeem rewards, and level up your membership</p>
      </div>

      {/* Points Overview */}
      <div className="grid grid-3">
        <div className="stat-card points-card">
          <div className="stat-icon">💎</div>
          <div className="stat-content">
            <h3>Current Points</h3>
            <div className="stat-value">{profile?.points || 0}</div>
            <p>Available to redeem</p>
          </div>
        </div>

        <div className="stat-card lifetime-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <h3>Lifetime Points</h3>
            <div className="stat-value">{profile?.lifetimePoints || 0}</div>
            <p>Total earned</p>
          </div>
        </div>

        <div className="stat-card tier-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>Current Tier</h3>
            <div className="stat-value" style={{ color: tierInfo?.currentTier?.color }}>
              {tierInfo?.currentTier?.name}
            </div>
            <p>{tierInfo?.currentTier?.multiplier}x points multiplier</p>
          </div>
        </div>
      </div>

      {/* Tier Progress */}
      {tierInfo?.nextTier && (
        <div className="card tier-progress-card">
          <h3>Progress to {tierInfo.nextTier.name}</h3>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${Math.min(tierInfo.progress, 100)}%`,
                background: tierInfo.nextTier.color
              }}
            ></div>
          </div>
          <p className="progress-text">
            {tierInfo.pointsToNextTier} more points to reach {tierInfo.nextTier.name}
          </p>
        </div>
      )}

      {/* Active Promotions */}
      {promotions.length > 0 && (
        <div className="card promotions-card">
          <h3>🎁 Active Promotions</h3>
          <div className="promotions-list">
            {promotions.map(promo => (
              <div key={promo.id} className="promotion-item">
                <div className="promotion-badge">HOT</div>
                <div>
                  <h4>{promo.name}</h4>
                  <p>{promo.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How to Earn Points - Customer Info */}
      <div className="card earn-points-card">
        <h3>💳 How to Earn Points</h3>
        <div className="earn-info">
          <p>Show your <strong>Membership Card</strong> at checkout to earn points on every purchase!</p>
          <ul className="earn-list">
            <li>📱 Open your <strong>Membership Card</strong> from the menu</li>
            <li>🛒 Show the QR code when checking out</li>
            <li>✨ Earn <strong>{tierInfo?.currentTier?.multiplier}x points</strong> per dollar spent</li>
            <li>🎁 Use points to redeem exclusive rewards</li>
          </ul>
          <a href="/membership" className="btn btn-primary">
            📱 View My Membership Card
          </a>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-2">
        <div className="card action-card">
          <h3>🎁 Redeem Rewards</h3>
          <p>Browse our rewards catalog and redeem your points</p>
          <a href="/rewards" className="btn btn-primary">View Rewards</a>
        </div>

        <div className="card action-card">
          <h3>📊 View History</h3>
          <p>Check your points history and transactions</p>
          <a href="/history" className="btn btn-secondary">View History</a>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
